import { useState, useEffect, useRef } from 'react';
import { FaPen, FaCheck } from 'react-icons/fa';
import SurahList from '../components/quran/SurahList';
import QuranReader from '../components/quran/QuranReader';
import ReciterSelector from '../components/quran/ReciterSelector';
import BookmarkManager from '../components/quran/BookmarkManager';
import apiClient from '../api/apiClient';
import useAuth from '../hooks/useAuth';

const QuranTracker = () => {
    const { user } = useAuth();
    const [selectedSurah, setSelectedSurah] = useState(1);
    const [selectedReciter, setSelectedReciter] = useState(
        user?.preferences?.preferredReciter || 'ar.alafasy'
    );
    const [bookmarks, setBookmarks] = useState([]);
    const [initialAyah, setInitialAyah] = useState(1);
    const mainPanelRef = useRef(null);

    const [stats, setStats] = useState(null);
    const [progressData, setProgressData] = useState(null);
    const [todayReadCount, setTodayReadCount] = useState(0);
    const [isEditingGoal, setIsEditingGoal] = useState(false);
    const [tempGoal, setTempGoal] = useState('');
    const [ayahConfetti, setAyahConfetti] = useState(false);
    const [showCompletionModal, setShowCompletionModal] = useState(false); // Refixed state

    useEffect(() => {
        // Trigger confetti only if goal reached EXACTLY AND modal is NOT open
        // Using === prevents repeated triggers on subsequent verses
        if (progressData && todayReadCount === (progressData.dailyAyahGoal || 20) && todayReadCount > 0) {
            if (!showCompletionModal) {
                setAyahConfetti(true);
                const timer = setTimeout(() => setAyahConfetti(false), 2000);
                return () => clearTimeout(timer);
            }
        }
    }, [todayReadCount, progressData, showCompletionModal]);

    useEffect(() => {
        fetchBookmarks();
        fetchProgress();
        fetchStats();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const refreshAfterRead = async () => {
        await Promise.all([fetchProgress(), fetchStats()]);
    };

    const fetchBookmarks = async () => {
        try {
            const res = await apiClient.get('/quran/bookmarks');
            setBookmarks(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error('Error fetching bookmarks', error);
        }
    };

    const fetchStats = async () => {
        try {
            const res = await apiClient.get('/quran/stats');
            setStats(res.data);

            // Calculate today's read count
            if (res.data.dailyReadings) {
                const today = new Date().setHours(0, 0, 0, 0);
                const todayEntry = res.data.dailyReadings.find(
                    (entry) => new Date(entry.date).setHours(0, 0, 0, 0) === today
                );
                setTodayReadCount(todayEntry ? todayEntry.ayahsCount : 0);
            }
        } catch (error) {
            console.error('Error fetching stats', error);
        }
    };

    const fetchProgress = async () => {
        try {
            const res = await apiClient.get('/quran/progress');
            if (res.data) {
                setProgressData(res.data);
            }
        } catch (error) {
            console.error('Error fetching progress', error);
        }
    };

    const scrollToReader = () => {
        if (mainPanelRef.current) {
            mainPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const handleSurahSelect = (surahNumber) => {
        setSelectedSurah(surahNumber);
        setInitialAyah(1);
        scrollToReader();
    };

    const handleReciterSelect = async (reciterId) => {
        setSelectedReciter(reciterId);
        // Update user preference
        try {
            await apiClient.put('/users/me', {
                preferences: { preferredReciter: reciterId },
            });
        } catch (error) {
            console.error('Error updating preference', error);
        }
    };

    const handleDeleteBookmark = async (id) => {
        try {
            await apiClient.delete(`/quran/bookmarks/${id}`);
            setBookmarks(bookmarks.filter((b) => b._id !== id));
        } catch (error) {
            console.error('Error deleting bookmark', error);
        }
    };

    const handleGoToBookmark = (surah, ayah) => {
        setSelectedSurah(surah);
        setInitialAyah(ayah);
        scrollToReader();
    };

    const handleUpdateGoal = async () => {
        if (!tempGoal || isNaN(parseInt(tempGoal))) {
            // Reset if invalid
            setTempGoal(progressData.dailyAyahGoal || 20);
            setIsEditingGoal(false);
            return;
        }

        try {
            const res = await apiClient.put('/quran/progress', {
                dailyAyahGoal: parseInt(tempGoal),
            });
            setProgressData(res.data);
            setIsEditingGoal(false);
        } catch (error) {
            console.error('Error updating goal', error);
        }
    };

    const startEditing = () => {
        // Ensure we have a valid value, default to 20 if undefined
        setTempGoal(progressData.dailyAyahGoal || 20);
        setIsEditingGoal(true);
    };

    return (
        <div className="quran-tracker-container">
            <div className="quran-sidebar-panel">
                <div className="stats-summary">
                    <h3>Vos Progrès</h3>
                    {stats && progressData ? (
                        <div className="stats-content">
                            <div className="daily-goal-section">
                                <h4>Objectif Quotidien</h4>
                                <div className={`daily-progress-wrapper ${ayahConfetti ? 'confetti-active' : ''}`} style={{ width: '100%' }}>
                                    <div className="progress-bar-container">
                                        <div
                                            className="progress-bar-fill"
                                            style={{ width: `${Math.min((todayReadCount / (progressData.dailyAyahGoal || 1)) * 100, 100)}%` }}
                                        ></div>
                                    </div>
                                </div>
                                <p className="goal-text">
                                    {todayReadCount} / {progressData.dailyAyahGoal || 20} Versets
                                </p>
                                <div className="goal-input">
                                    <label>Définir l'objectif : </label>
                                    {isEditingGoal ? (
                                        <div className="goal-edit-wrapper">
                                            <input
                                                type="number"
                                                min="1"
                                                value={tempGoal}
                                                onChange={(e) => setTempGoal(e.target.value)}
                                                className="goal-input-field"
                                                autoFocus
                                            />
                                            <button className="btn-icon-small" onClick={handleUpdateGoal}>
                                                <FaCheck />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="goal-display-wrapper">
                                            <span>{progressData.dailyAyahGoal || 20}</span>
                                            <button className="btn-icon-small" onClick={startEditing}>
                                                <FaPen />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="stats-grid">
                                <div className="stat-item">
                                    <span className="stat-value">{stats.totalAyahsRead}</span>
                                    <span className="stat-label">Total Lus</span>
                                </div>
                                <div className="stat-item">
                                    <span className="stat-value">{stats.completedSurahsCount}</span>
                                    <span className="stat-label">Sourates Terminées</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <p>Loading stats...</p>
                    )}
                </div>
                <ReciterSelector
                    selectedReciter={selectedReciter}
                    onSelectReciter={handleReciterSelect}
                />
                <BookmarkManager
                    bookmarks={bookmarks}
                    onDelete={handleDeleteBookmark}
                    onGoTo={handleGoToBookmark}
                />
                <SurahList
                    onSelectSurah={handleSurahSelect}
                    selectedSurah={selectedSurah}
                />
            </div>
            <div className="quran-main-panel" ref={mainPanelRef}>
                <QuranReader
                    surahNumber={selectedSurah}
                    initialAyah={initialAyah}
                    reciterId={selectedReciter}
                    onProgressUpdate={refreshAfterRead}
                    bookmarks={bookmarks}
                    setBookmarks={setBookmarks}
                    showCompletionModal={showCompletionModal}
                    setShowCompletionModal={setShowCompletionModal}
                />
            </div>
        </div>
    );
};

export default QuranTracker;
